import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  InjunctionAnswer,
  InjunctionSuspension,
  useRecordContractConcludedMutation,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { ConcludedDateField } from "./ConcludedDateField";
import type { StandstillEnd } from "./concludedDate";
import { concludedDateProblem } from "./concludedDate";
import { InjunctionQuestion, injunctionAnswerMissing, injunctionField } from "./InjunctionQuestion";

interface Props {
  eventId: string;
  concludedOn: string | null | undefined;
  awardDecisionDay: string | null;
  standstill: StandstillEnd | null;
  contractFrom?: string | null;
  injunctionSuspension?: InjunctionSuspension | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ContractConcludedModal = ({
  eventId,
  concludedOn,
  awardDecisionDay,
  standstill,
  contractFrom = null,
  injunctionSuspension = null,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [date, setDate] = useState(concludedOn ?? "");
  const [injunction, setInjunction] = useState<InjunctionAnswer | null>(null);
  const [record, { isLoading }] = useRecordContractConcludedMutation();
  const asksInjunction = injunctionSuspension != null && date !== (concludedOn ?? "");

  const save = async () => {
    try {
      await record({
        eventId,
        contractConcludedRequest: { contractConcludedOn: date, ...injunctionField(asksInjunction, injunction) },
      }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {t("When was the contract concluded?")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <ConcludedDateField
            id="contract-concluded-on"
            value={date}
            onChange={setDate}
            earliest={awardDecisionDay}
            standstill={standstill}
            contractFrom={contractFrom}
            helperText={
              t(
                "The deadline for announcing the contract award on Doffin is counted from this date",
              ) ?? undefined
            }
          />
          {asksInjunction && injunctionSuspension != null && (
            <Box pt="6">
              <InjunctionQuestion suspension={injunctionSuspension} answer={injunction} onChange={setInjunction} />
            </Box>
          )}
          <Flex justify="end" pt="6">
            <Button
              colorScheme="teal"
              isDisabled={
                date === "" ||
                injunctionAnswerMissing(asksInjunction, injunction) ||
                concludedDateProblem(date, awardDecisionDay, undefined, contractFrom) != null
              }
              isLoading={isLoading}
              onClick={save}
            >
              {t("Save")}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
